/*
CSS: national-scope-selector.css
─────────────────────────────────────────────────────────────────────────────
.nss-root { display: flex; flex-direction: column; gap: 1.25rem; }

.nss-scope-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.nss-scope-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.04);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-size: 0.88rem;
  color: rgba(255,255,255,0.8);
}

.nss-scope-option:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.2); }

.nss-scope-option.nss-active {
  background: rgba(0,191,255,0.12);
  border-color: rgba(0,191,255,0.45);
  color: #00BFFF;
}

.nss-scope-option input[type="radio"] { accent-color: #00BFFF; flex-shrink: 0; }

.nss-bbox-hint {
  font-size: 0.83rem;
  color: rgba(255,255,255,0.45);
  padding: 0.4rem 0.6rem;
  border: 1px dashed rgba(255,255,255,0.15);
  border-radius: 6px;
}

.nss-field-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.55);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.nss-select {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.18);
  color: #fff;
  font-size: 0.9rem;
  cursor: pointer;
}

.nss-select:disabled { opacity: 0.45; cursor: not-allowed; }

.nss-loading-hint {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.35);
  margin: 0;
}

.nss-checklist-actions { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; flex-wrap: wrap; }

.nss-btn-ghost {
  font-size: 0.78rem;
  padding: 0.28rem 0.6rem;
  border-radius: 4px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.75);
  cursor: pointer;
}

.nss-btn-ghost:hover { background: rgba(255,255,255,0.14); }

.nss-checklist {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 6px;
  padding: 0.2rem;
}

.nss-checklist-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.38rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.87rem;
  color: rgba(255,255,255,0.82);
  transition: background 0.1s;
}

.nss-checklist-item:hover { background: rgba(255,255,255,0.05); }
.nss-checklist-item.nss-checked { background: rgba(0,191,255,0.08); color: #00BFFF; }
.nss-checklist-item input[type="checkbox"] { accent-color: #00BFFF; flex-shrink: 0; }

.nss-min-hint { font-size: 0.78rem; color: rgba(255,255,255,0.35); margin: 0.3rem 0 0; }

.nss-coverage {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  border: 1px solid;
}

.nss-coverage.nss-coverage-ok {
  background: rgba(34,197,94,0.1);
  border-color: rgba(34,197,94,0.35);
  color: #4ade80;
}

.nss-coverage.nss-coverage-none {
  background: rgba(239,68,68,0.1);
  border-color: rgba(239,68,68,0.35);
  color: #f87171;
}

.nss-coverage.nss-coverage-pending {
  background: rgba(251,191,36,0.08);
  border-color: rgba(251,191,36,0.3);
  color: #fbbf24;
}

.nss-coverage.nss-coverage-loading {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.35);
}

.nss-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }

.nss-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  background: rgba(0,191,255,0.12);
  color: #00BFFF;
  border: 1px solid rgba(0,191,255,0.3);
}
─────────────────────────────────────────────────────────────────────────────
*/

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './national-scope-selector.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NationalScopeType = 'country' | 'admin_single' | 'admin_multi' | 'custom_bbox';

export interface NationalScopeState {
  countryCode: string;
  scopeType: NationalScopeType;
  scopeAdminLevel: 1 | 2 | null;
  scopeBoundaryCode: string | null;
  scopeBoundaryCodes: string[];
  selectedNames: string[];
  coverageChecked: boolean;
  coverage: { available: boolean; edgeCount: number; nodeCount: number } | null;
}

export interface NationalScopeSelectorProps {
  value: NationalScopeState;
  onChange: (state: NationalScopeState) => void;
  isLoading: boolean;
}

interface TerritorialBoundary {
  id: string;
  adminCode: string;
  adminName: string;
  adminLevel: number;
  parentAdminCode: string | null;
  bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number } | null;
}

interface CoverageResult {
  available: boolean;
  edgeCount: number;
  nodeCount: number;
  territorialCoverageStatus: 'ready' | 'no_coverage';
}

// ─── Country catalog ──────────────────────────────────────────────────────────

type CountryStatus = 'available' | 'pending_jurisdictions' | 'pending_network';

interface SupportedCountry {
  code: string;
  name: string;
  status: CountryStatus;
}

const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  { code: 'ARG', name: 'Argentina', status: 'available' },
  { code: 'MEX', name: 'México', status: 'available' },
];

// Labels for level-1 and level-2 boundaries per country
const COUNTRY_CONFIG: Record<string, {
  level1: string; level1Multi: string; article1: string;
  level2: string; level2Multi: string; article2: string;
  national: string;
}> = {
  ARG: {
    level1: 'Provincia',    level1Multi: 'Provincias', article1: 'una',
    level2: 'Departamento / Municipio', level2Multi: 'Departamentos / Municipios', article2: 'un',
    national: 'Argentina',
  },
  MEX: {
    level1: 'Estado',       level1Multi: 'Estados',    article1: 'un',
    level2: 'Municipio',    level2Multi: 'Municipios', article2: 'un',
    national: 'México',
  },
};

function getCountryConfig(code: string) {
  return COUNTRY_CONFIG[code] ?? {
    level1: 'Provincia / Estado', level1Multi: 'Provincias / Estados', article1: 'una',
    level2: 'Municipio / Departamento', level2Multi: 'Municipios / Departamentos', article2: 'un',
    national: code,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthToken = (): string | null => {
  const auth = localStorage.getItem('auth-storage');
  if (!auth) return null;
  try {
    return (JSON.parse(auth) as { state?: { token?: string } }).state?.token ?? null;
  } catch {
    return null;
  }
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Whether a scope selection is "complete enough" to trigger a coverage check
function isSelectionComplete(v: NationalScopeState): boolean {
  if (v.scopeType === 'country') return true;
  if (v.scopeType === 'admin_single') return !!v.scopeBoundaryCode;
  if (v.scopeType === 'admin_multi') return v.scopeBoundaryCodes.length >= 2;
  return false;
}

// Build coverage check payload
function buildCoveragePayload(v: NationalScopeState): Record<string, unknown> {
  const base = { countryCode: v.countryCode, territorialScopeType: v.scopeType };
  if (v.scopeType === 'country') return base;
  if (v.scopeType === 'admin_single') {
    return { ...base, adminLevel: v.scopeAdminLevel, scopeBoundaryCode: v.scopeBoundaryCode };
  }
  // admin_multi
  return { ...base, adminLevel: v.scopeAdminLevel, scopeBoundaryCodes: v.scopeBoundaryCodes };
}

// ─── Scope option definitions ─────────────────────────────────────────────────

interface ScopeOption {
  label: string;
  scopeType: NationalScopeType;
  adminLevel: 1 | 2 | null;
}

const SCOPE_OPTIONS: ScopeOption[] = [
  { label: 'Local / Municipal',   scopeType: 'admin_single', adminLevel: 2 },
  { label: 'Multijurisdiccional', scopeType: 'admin_multi',  adminLevel: 2 },
  { label: 'Provincial / Estatal',scopeType: 'admin_single', adminLevel: 1 },
  { label: 'Regional',            scopeType: 'admin_multi',  adminLevel: 1 },
  { label: 'Nacional',            scopeType: 'country',      adminLevel: null },
  { label: 'Personalizado',       scopeType: 'custom_bbox',  adminLevel: null },
];

function optionKey(o: ScopeOption): string {
  return `${o.scopeType}-${o.adminLevel ?? 'null'}`;
}

function valueKey(v: NationalScopeState): string {
  return `${v.scopeType}-${v.scopeAdminLevel ?? 'null'}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function NationalScopeSelector({ value, onChange, isLoading }: NationalScopeSelectorProps): JSX.Element {
  const [provinces, setProvinces] = useState<TerritorialBoundary[]>([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);

  // selectedProvince tracks which province is chosen for dept loading (single or multi level-2)
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>('');
  const [departments, setDepartments] = useState<TerritorialBoundary[]>([]);
  const [loadingDepts, setLoadingDepts] = useState(false);

  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load provinces when country changes ─────────────────────────────────
  useEffect(() => {
    if (!value.countryCode) return;
    setLoadingProvinces(true);
    setProvinces([]);
    axios
      .get<TerritorialBoundary[]>(`${API_URL}/territorial-boundaries`, {
        params: { country: value.countryCode, level: 1 },
        headers: authHeaders(),
      })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) =>
          a.adminName.localeCompare(b.adminName, 'es')
        );
        setProvinces(sorted);
      })
      .catch((err) => console.error('[NationalScopeSelector] Error cargando provincias:', err))
      .finally(() => setLoadingProvinces(false));
  }, [value.countryCode]);

  // ── Load departments when selectedProvinceCode changes ───────────────────
  useEffect(() => {
    if (!selectedProvinceCode || value.scopeAdminLevel !== 2) {
      setDepartments([]);
      return;
    }
    setLoadingDepts(true);
    axios
      .get<TerritorialBoundary[]>(`${API_URL}/territorial-boundaries`, {
        params: { country: value.countryCode, level: 2, parent: selectedProvinceCode },
        headers: authHeaders(),
      })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) =>
          a.adminName.localeCompare(b.adminName, 'es')
        );
        setDepartments(sorted);
      })
      .catch((err) => console.error('[NationalScopeSelector] Error cargando departamentos:', err))
      .finally(() => setLoadingDepts(false));
  }, [selectedProvinceCode, value.scopeAdminLevel]);

  // ── Debounced coverage check ──────────────────────────────────────────────
  const triggerCoverageCheck = useCallback(
    (state: NationalScopeState) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!isSelectionComplete(state)) return;

      debounceRef.current = setTimeout(async () => {
        setCheckingCoverage(true);
        try {
          const res = await axios.post<CoverageResult>(
            `${API_URL}/admin/projects/national-coverage-check`,
            buildCoveragePayload(state),
            { headers: authHeaders() }
          );
          const { available, edgeCount, nodeCount } = res.data;
          onChange({
            ...state,
            coverageChecked: true,
            coverage: { available, edgeCount, nodeCount },
          });
        } catch (err) {
          console.error('[NationalScopeSelector] Error verificando cobertura:', err);
          onChange({ ...state, coverageChecked: true, coverage: null });
        } finally {
          setCheckingCoverage(false);
        }
      }, 600);
    },
    [onChange]
  );

  // ── Country change ───────────────────────────────────────────────────────
  const handleCountryChange = useCallback(
    (newCode: string) => {
      setSelectedProvinceCode('');
      setDepartments([]);
      onChange({
        countryCode: newCode,
        scopeType: 'admin_single',
        scopeAdminLevel: 2,
        scopeBoundaryCode: null,
        scopeBoundaryCodes: [],
        selectedNames: [],
        coverageChecked: false,
        coverage: null,
      });
    },
    [onChange]
  );

  // ── Scope type / admin level selection ───────────────────────────────────
  const handleScopeOptionSelect = useCallback(
    (opt: ScopeOption) => {
      if (opt.scopeType === 'custom_bbox') {
        onChange({ ...value, scopeType: 'custom_bbox' });
        return;
      }
      const next: NationalScopeState = {
        countryCode: value.countryCode,
        scopeType: opt.scopeType,
        scopeAdminLevel: opt.adminLevel,
        scopeBoundaryCode: null,
        scopeBoundaryCodes: [],
        selectedNames: [],
        coverageChecked: false,
        coverage: null,
      };
      setSelectedProvinceCode('');
      setDepartments([]);

      if (opt.scopeType === 'country') {
        triggerCoverageCheck(next);
      }
      onChange(next);
    },
    [value, onChange, triggerCoverageCheck]
  );

  // ── Province change (single select — level 1 or level 2 parent) ──────────
  const handleProvinceSingleSelect = useCallback(
    (adminCode: string) => {
      const province = provinces.find((p) => p.adminCode === adminCode);
      const name = province?.adminName ?? adminCode;

      if (value.scopeAdminLevel === 1) {
        // admin_single level=1: province IS the boundary
        const next: NationalScopeState = {
          ...value,
          scopeBoundaryCode: adminCode || null,
          selectedNames: adminCode ? [name] : [],
          coverageChecked: false,
          coverage: null,
        };
        onChange(next);
        if (adminCode) triggerCoverageCheck(next);
      } else if (value.scopeAdminLevel === 2) {
        // Province used only to filter depts
        setSelectedProvinceCode(adminCode);
        const next: NationalScopeState = {
          ...value,
          scopeBoundaryCode: null,
          selectedNames: [],
          coverageChecked: false,
          coverage: null,
        };
        onChange(next);
      }
    },
    [value, provinces, onChange, triggerCoverageCheck]
  );

  // ── Province checkbox toggle (admin_multi level=1) ───────────────────────
  const handleProvinceMultiToggle = useCallback(
    (adminCode: string) => {
      const province = provinces.find((p) => p.adminCode === adminCode);
      const name = province?.adminName ?? adminCode;

      const codes = value.scopeBoundaryCodes.includes(adminCode)
        ? value.scopeBoundaryCodes.filter((c) => c !== adminCode)
        : [...value.scopeBoundaryCodes, adminCode];

      const names = codes
        .map((c) => provinces.find((p) => p.adminCode === c)?.adminName ?? c);

      const next: NationalScopeState = {
        ...value,
        scopeBoundaryCodes: codes,
        selectedNames: names,
        coverageChecked: false,
        coverage: null,
      };
      // Suppress 'name' unused lint — it's used inside block above via province.adminName
      void name;
      onChange(next);
      triggerCoverageCheck(next);
    },
    [value, provinces, onChange, triggerCoverageCheck]
  );

  // ── Dept select (admin_single level=2) ───────────────────────────────────
  const handleDeptSingleSelect = useCallback(
    (adminCode: string) => {
      const dept = departments.find((d) => d.adminCode === adminCode);
      const name = dept?.adminName ?? adminCode;
      const next: NationalScopeState = {
        ...value,
        scopeBoundaryCode: adminCode || null,
        selectedNames: adminCode ? [name] : [],
        coverageChecked: false,
        coverage: null,
      };
      onChange(next);
      if (adminCode) triggerCoverageCheck(next);
    },
    [value, departments, onChange, triggerCoverageCheck]
  );

  // ── Dept checkbox toggle (admin_multi level=2) ────────────────────────────
  const handleDeptMultiToggle = useCallback(
    (adminCode: string) => {
      const codes = value.scopeBoundaryCodes.includes(adminCode)
        ? value.scopeBoundaryCodes.filter((c) => c !== adminCode)
        : [...value.scopeBoundaryCodes, adminCode];

      const names = codes
        .map((c) => departments.find((d) => d.adminCode === c)?.adminName ?? c);

      const next: NationalScopeState = {
        ...value,
        scopeBoundaryCodes: codes,
        selectedNames: names,
        coverageChecked: false,
        coverage: null,
      };
      onChange(next);
      triggerCoverageCheck(next);
    },
    [value, departments, onChange, triggerCoverageCheck]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  const currentKey = valueKey(value);
  const showProvinces = value.scopeAdminLevel === 1 || value.scopeAdminLevel === 2;
  const showDepts = value.scopeAdminLevel === 2 && !!selectedProvinceCode;
  const isMulti = value.scopeType === 'admin_multi';
  const cfg = getCountryConfig(value.countryCode);

  return (
    <div className="nss-root">
      {/* ── Country selector ── */}
      <div>
        <label className="nss-field-label">País</label>
        <select
          className="nss-select"
          value={value.countryCode}
          disabled={isLoading}
          onChange={(e) => handleCountryChange(e.target.value)}
        >
          {SUPPORTED_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code} disabled={c.status !== 'available'}>
              {c.name}{c.status !== 'available' ? ' (jurisdicciones pendientes)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* ── Scope type radio grid ── */}
      <div className="nss-scope-grid">
        {SCOPE_OPTIONS.map((opt) => {
          const key = optionKey(opt);
          const active = key === currentKey;
          return (
            <label key={key} className={`nss-scope-option${active ? ' nss-active' : ''}`}>
              <input
                type="radio"
                name="nss-scope-type"
                checked={active}
                disabled={isLoading}
                onChange={() => handleScopeOptionSelect(opt)}
              />
              {opt.label}
            </label>
          );
        })}
      </div>

      {/* ── Nacional: texto informativo ── */}
      {value.scopeType === 'country' && (
        <p className="nss-bbox-hint">
          El proyecto utilizará la cobertura nacional disponible para {cfg.national}.
        </p>
      )}

      {/* ── custom_bbox hint ── */}
      {value.scopeType === 'custom_bbox' && (
        <p className="nss-bbox-hint">Dibujá el recorte en el mapa o usá el selector de límite administrativo</p>
      )}

      {/* ── Province selector ── */}
      {showProvinces && value.scopeType !== 'custom_bbox' && (
        <div>
          <label className="nss-field-label">
            {value.scopeAdminLevel === 1
              ? isMulti ? cfg.level1Multi : cfg.level1
              : `${cfg.level1} (para filtrar ${cfg.level2Multi.toLowerCase()})`}
          </label>
          {!loadingProvinces && provinces.length === 0 && (
            <p className="nss-bbox-hint">
              Las jurisdicciones administrativas no están cargadas en este ambiente.
              Usá <strong>Personalizado</strong> para definir el área manualmente,
              o pedile al administrador que ejecute el seed de límites territoriales.
            </p>
          )}
          {loadingProvinces ? (
            <p className="nss-loading-hint">Cargando provincias...</p>
          ) : isMulti && value.scopeAdminLevel === 1 ? (
            // Multi-provincia checklist
            <>
              <div className="nss-checklist-actions">
                <button
                  type="button"
                  className="nss-btn-ghost"
                  disabled={isLoading}
                  onClick={() => {
                    const codes = provinces.map((p) => p.adminCode);
                    const names = provinces.map((p) => p.adminName);
                    const next: NationalScopeState = {
                      ...value,
                      scopeBoundaryCodes: codes,
                      selectedNames: names,
                      coverageChecked: false,
                      coverage: null,
                    };
                    onChange(next);
                    triggerCoverageCheck(next);
                  }}
                >
                  Seleccionar todos
                </button>
                {value.scopeBoundaryCodes.length > 0 && (
                  <button
                    type="button"
                    className="nss-btn-ghost"
                    disabled={isLoading}
                    onClick={() => {
                      onChange({ ...value, scopeBoundaryCodes: [], selectedNames: [], coverageChecked: false, coverage: null });
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
              <div className="nss-checklist">
                {provinces.map((p) => {
                  const checked = value.scopeBoundaryCodes.includes(p.adminCode);
                  return (
                    <label
                      key={p.adminCode}
                      className={`nss-checklist-item${checked ? ' nss-checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isLoading}
                        onChange={() => handleProvinceMultiToggle(p.adminCode)}
                      />
                      {p.adminName}
                    </label>
                  );
                })}
              </div>
              {value.scopeBoundaryCodes.length > 0 && value.scopeBoundaryCodes.length < 2 && (
                <p className="nss-min-hint">Seleccioná al menos 2 {cfg.level1Multi.toLowerCase()}</p>
              )}
            </>
          ) : (
            // Single province select (for admin_single level 1 or 2)
            <select
              className="nss-select"
              value={value.scopeAdminLevel === 1 ? (value.scopeBoundaryCode ?? '') : selectedProvinceCode}
              disabled={isLoading}
              onChange={(e) => handleProvinceSingleSelect(e.target.value)}
            >
              <option value="">— Seleccioná {isMulti ? cfg.level1Multi.toLowerCase() : `${cfg.article1} ${cfg.level1.toLowerCase()}`} —</option>
              {provinces.map((p) => (
                <option key={p.adminCode} value={p.adminCode}>
                  {p.adminName}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* ── Department selector ── */}
      {showDepts && value.scopeType !== 'custom_bbox' && (
        <div>
          <label className="nss-field-label">
            {isMulti ? cfg.level2Multi : cfg.level2}
          </label>
          {loadingDepts ? (
            <p className="nss-loading-hint">Cargando departamentos...</p>
          ) : isMulti ? (
            <>
              <div className="nss-checklist-actions">
                <button
                  type="button"
                  className="nss-btn-ghost"
                  disabled={isLoading}
                  onClick={() => {
                    const codes = departments.map((d) => d.adminCode);
                    const names = departments.map((d) => d.adminName);
                    const next: NationalScopeState = {
                      ...value,
                      scopeBoundaryCodes: codes,
                      selectedNames: names,
                      coverageChecked: false,
                      coverage: null,
                    };
                    onChange(next);
                    triggerCoverageCheck(next);
                  }}
                >
                  Seleccionar todos
                </button>
                {value.scopeBoundaryCodes.length > 0 && (
                  <button
                    type="button"
                    className="nss-btn-ghost"
                    disabled={isLoading}
                    onClick={() =>
                      onChange({ ...value, scopeBoundaryCodes: [], selectedNames: [], coverageChecked: false, coverage: null })
                    }
                  >
                    Limpiar
                  </button>
                )}
              </div>
              <div className="nss-checklist">
                {departments.map((d) => {
                  const checked = value.scopeBoundaryCodes.includes(d.adminCode);
                  return (
                    <label
                      key={d.adminCode}
                      className={`nss-checklist-item${checked ? ' nss-checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={isLoading}
                        onChange={() => handleDeptMultiToggle(d.adminCode)}
                      />
                      {d.adminName}
                    </label>
                  );
                })}
              </div>
              {value.scopeBoundaryCodes.length > 0 && value.scopeBoundaryCodes.length < 2 && (
                <p className="nss-min-hint">Seleccioná al menos 2 {cfg.level2Multi.toLowerCase()}</p>
              )}
            </>
          ) : (
            <select
              className="nss-select"
              value={value.scopeBoundaryCode ?? ''}
              disabled={isLoading}
              onChange={(e) => handleDeptSingleSelect(e.target.value)}
            >
              <option value="">— Seleccioná {isMulti ? cfg.level2Multi.toLowerCase() : `${cfg.article2} ${cfg.level2.toLowerCase()}`} —</option>
              {departments.map((d) => (
                <option key={d.adminCode} value={d.adminCode}>
                  {d.adminName}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* ── Coverage result ── */}
      {value.scopeType !== 'custom_bbox' && (checkingCoverage || value.coverageChecked) && (
        <div
          className={`nss-coverage${
            checkingCoverage
              ? ' nss-coverage-loading'
              : value.coverage === null
              ? ' nss-coverage-none'
              : value.coverage.available
              ? ' nss-coverage-ok'
              : value.coverage.edgeCount === 0
              ? ' nss-coverage-pending'
              : ' nss-coverage-none'
          }`}
        >
          {checkingCoverage
            ? 'Verificando cobertura...'
            : value.coverage === null
            ? 'No se pudo verificar la cobertura por un error técnico'
            : value.coverage.available
            ? `Cobertura disponible — ${value.coverage.nodeCount.toLocaleString('es-AR')} nodos · ${value.coverage.edgeCount.toLocaleString('es-AR')} tramos`
            : value.coverage.edgeCount === 0
            ? 'No hay red urbana cargada para este municipio'
            : 'Sin cobertura confirmada para este alcance'}
        </div>
      )}

      {/* ── Selected chips ── */}
      {value.selectedNames.length > 0 && value.scopeType !== 'custom_bbox' && (
        <div className="nss-chips">
          {value.selectedNames.map((name) => (
            <span key={name} className="nss-chip">
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
